<template>
  <div class="pwa-login-page">
    <LodingComponent :is-visible="loading" text="로그인 중..." />
    
    <div class="login-container">
      <!-- 로고 영역 -->
      <div class="logo-section">
        <img :src="logoImage" alt="하이파이브" class="logo-image">
        <h1 class="app-title">하이파이브</h1>
        <p class="app-subtitle">재가장기요양센터 관리 시스템</p>
      </div>

      <!-- 로그인 폼 -->
      <div class="form-section">
        <v-form ref="loginForm" @submit.prevent="handleLogin">
          <div class="input-group">
            <v-icon class="input-icon">mdi-account</v-icon>
            <input
              v-model="loginData.loginId"
              type="text"
              class="form-input"
              placeholder="아이디"
              :disabled="loading"
              autocomplete="username"
            />
          </div>
          
          <div class="input-group">
            <v-icon class="input-icon">mdi-lock</v-icon>
            <input
              v-model="loginData.loginPw"
              type="password"
              class="form-input"
              placeholder="비밀번호"
              :disabled="loading"
              autocomplete="current-password"
              @keyup.enter="handleLogin"
            />
          </div>

          <div class="checkbox-group">
            <label class="checkbox-label">
              <input
                v-model="saveId"
                type="checkbox"
                class="checkbox-input"
                :disabled="loading"
              />
              <span class="checkbox-text">아이디 저장</span>
            </label>
          </div>

          <button
            type="button"
            class="login-button"
            :disabled="!isFormValid || loading"
            :class="{ 'loading': loading }"
            @click="handleLogin"
          >
            <span v-if="!loading">로그인</span>
            <span v-else class="loading-text">로그인 중...</span>
          </button>
        </v-form>

        <div class="divider">
          <span>또는</span>
        </div>

        <button
          type="button"
          class="signup-button"
          :disabled="loading"
          @click="handleSignup"
        >
          회원가입
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import LodingComponent from '@/components/loading/LodingComponent.vue';

interface LoginData {
  loginId: string;
  loginPw: string;
}

const store = useStore();
const router = useRouter();

const logoImage = ref(new URL('../../assets/images/hypehive-logo.png', import.meta.url).href);

const loginData = ref<LoginData>({
  loginId: '',
  loginPw: ''
});

const saveId = ref(false);
const loginForm = ref();
const isSubmitting = ref(false);

const isFormValid = computed(() => {
  return loginData.value.loginId.trim() !== '' && 
         loginData.value.loginPw.trim() !== '';
});

const loading = computed(() => store.getters['pwa/pwaLoginLoading'] || isSubmitting.value);

const handleLogin = async () => {
  if (!isFormValid.value || isSubmitting.value) return;
  
  isSubmitting.value = true;
  
  try {
    // PWA 전용 로그인 사용 (hh_member + hh_emp 통합)
    const result = await store.dispatch('pwa/performPwaLogin', {
      loginId: loginData.value.loginId,
      loginPw: loginData.value.loginPw
    });
    
    if (result.success) {
      if (saveId.value) {
        localStorage.setItem('savedLoginId', loginData.value.loginId);
      } else {
        localStorage.removeItem('savedLoginId');
      }
      
      // 사용자 타입에 따라 다른 경로로 이동
      // manager: 센터장/관리자 (hh_member) -> /pwa/home
      // boss: 대표자 (hh_emp, emp_isboss='Y') -> /emp/home
      // employee: 일반 사원 (hh_emp) -> /emp/home
      
      // userType에 따라 적절한 홈으로 이동
      if (result.userType === 'employee' || result.userType === 'boss') {
        router.push('/emp/home');
      } else {
        router.push('/pwa/home');
      }
    } else {
      loginData.value.loginPw = '';
      setTimeout(() => {
        const passwordField = document.querySelector('input[type="password"]') as HTMLInputElement;
        if (passwordField) {
          passwordField.focus();
        }
      }, 100);
    }
  } catch (error: any) {
    console.error('로그인 오류:', error);
    loginData.value.loginPw = '';
    setTimeout(() => {
      const passwordField = document.querySelector('input[type="password"]') as HTMLInputElement;
      if (passwordField) {
        passwordField.focus();
      }
    }, 100);
  } finally {
    isSubmitting.value = false;
  }
};

const handleSignup = () => {
  router.push('/pwa/signup');
};

onMounted(() => {
  // 저장된 아이디 불러오기
  const savedLoginId = localStorage.getItem('savedLoginId');
  if (savedLoginId) {
    loginData.value.loginId = savedLoginId;
    saveId.value = true;
  }
  
  // PWA 로그인 상태 확인 (이미 로그인되어 있는지)
  // 주의: 로그인 페이지에서는 API 호출 없이 로컬 상태만 확인
  const pwaUser = store.getters['pwa/pwaUser'];
  if (pwaUser) {
    router.push('/pwa/home');
  }
});
</script>

<style scoped lang="scss">
.pwa-login-page {
  width: 100%;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
}

.login-container {
  width: 100%;
  max-width: 400px;
  background: #ffffff;
  border-radius: 24px;
  padding: 40px 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.logo-section {
  text-align: center;
  margin-bottom: 40px;
}

.logo-image {
  width: 80px;
  height: auto;
  margin-bottom: 16px;
}

.app-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
}

.app-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.form-section {
  width: 100%;
}

.input-group {
  position: relative;
  margin-bottom: 16px;
}

.input-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 20px;
  z-index: 1;
}

.form-input {
  width: 100%;
  height: 56px;
  padding: 0 16px 0 48px;
  font-size: 16px;
  color: #1a1a1a;
  background-color: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  box-sizing: border-box;
  transition: all 0.2s ease;
  font-family: 'Noto Sans KR', sans-serif;

  &:focus {
    outline: none;
    border-color: #667eea;
    background-color: #ffffff;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.checkbox-group {
  margin-bottom: 24px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.checkbox-input {
  width: 20px;
  height: 20px;
  margin-right: 8px;
  cursor: pointer;
  accent-color: #667eea;
}

.checkbox-text {
  font-size: 14px;
  color: #4b5563;
  font-family: 'Noto Sans KR', sans-serif;
}

.login-button {
  width: 100%;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Noto Sans KR', sans-serif;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &.loading {
    background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
  }
}

.loading-text {
  display: inline-block;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 24px 0;
  color: #9ca3af;
  font-size: 14px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e5e7eb;
  }

  &::before {
    margin-right: 12px;
  }

  &::after {
    margin-left: 12px;
  }
}

.signup-button {
  width: 100%;
  height: 56px;
  background: #ffffff;
  color: #667eea;
  font-size: 16px;
  font-weight: 600;
  border: 2px solid #667eea;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Noto Sans KR', sans-serif;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

@media (max-width: 480px) {
  .login-container {
    padding: 32px 24px;
    border-radius: 20px;
  }

  .app-title {
    font-size: 24px;
  }

  .form-input,
  .login-button,
  .signup-button {
    height: 52px;
    font-size: 15px;
  }
}
</style>

